import { ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';

import { FatalErrorOverlay } from '../components/v2-common/FatalErrorOverlay';

interface FatalErrorController {
  readonly displayFatalError: (message: string) => void;
}

const FatalErrorContext = createContext<FatalErrorController | undefined>(undefined);

/**
 * Renders its children normally until a caller invokes `displayFatalError`,
 * at which point a full-screen, non-dismissible overlay blocks the app and
 * the only action offered is to reload the page.
 */
export function FatalErrorProvider({ children }: { readonly children: ReactNode }) {
  const [message, setMessage] = useState<string | null>(null);

  const displayFatalError = useCallback((next: string) => {
    setMessage(next);
  }, []);

  const value = useMemo(() => ({ displayFatalError }), [displayFatalError]);

  return (
    <FatalErrorContext.Provider value={value}>
      {children}
      {message !== null && <FatalErrorOverlay message={message} />}
    </FatalErrorContext.Provider>
  );
}

/**
 * Returns `displayFatalError(message)`. Call it only when the app has hit a
 * situation with no local recovery — the user's single path forward is to
 * reload the page. The overlay blocks every other interaction until reload.
 */
export function useFatalError(): FatalErrorController {
  const context = useContext(FatalErrorContext);
  if (!context) {
    throw new Error('useFatalError must be used within a FatalErrorProvider');
  }
  return context;
}
